import { GetPaths, useClientPortalAPI } from "@certn/api";
import { Select, SelectItem } from "@certn/cascadia-react";
import { css } from "@emotion/css";
import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { components } from "@certn/api/generated-schemas/client-portal";
import { useOrderContext } from "../../../../pages/order/OrderContext";
import { SelectFieldDescription } from "./SelectFieldDescription";

type ReferenceTemplateOption = components["schemas"]["ReferenceOptionsList"];

type CheckListChild = components["schemas"]["CheckListChild"];
type UmbrellaCheckSelectOption =
  components["schemas"]["UmbrellaCheckSelectOption"];

export interface ReferenceQuantitySelectsProps {
  selectFieldDescription?: string[][];
  selectFieldEndpoint?: string | null;
  onUpdateSelection: (
    checkArguments: Record<string, unknown>,
    selectedOptions?: UmbrellaCheckSelectOption[]
  ) => void;
  isDisabled?: boolean;
  checkArguments?: Record<string, unknown>;
  checkChildren?: CheckListChild[];
  customerGroup?: string;
}

function makeSyntheticOption(
  child: CheckListChild | undefined,
  fallbackLabel: string,
  value: string
): UmbrellaCheckSelectOption {
  return {
    ordering_type: "REFERENCE",
    select_field_argument: "reference",
    label: child?.name ?? fallbackLabel,
    value,
    price: child?.price ?? 0,
    required_check_types: null,
    option_has_ordering_error_for_customer_group: null,
    description: null,
  };
}

function buildSyntheticOptions(
  children: CheckListChild[] | undefined,
  profQty: number,
  persQty: number,
  fallbackLabels: { professional: string; personal: string }
): UmbrellaCheckSelectOption[] {
  const profChild = children?.find(
    (c) => c.type === "PROFESSIONAL_REFERENCE_1"
  );
  const persChild = children?.find((c) => c.type === "PERSONAL_REFERENCE_1");

  if (profQty > 0 && !profChild) {
    console.error(
      "ReferenceQuantitySelects: PROFESSIONAL_REFERENCE_1 not found in checkChildren — price will default to 0"
    );
  }
  if (persQty > 0 && !persChild) {
    console.error(
      "ReferenceQuantitySelects: PERSONAL_REFERENCE_1 not found in checkChildren — price will default to 0"
    );
  }

  return [
    ...Array<null>(profQty)
      .fill(null)
      .map(() =>
        makeSyntheticOption(
          profChild,
          fallbackLabels.professional,
          "PROFESSIONAL_REFERENCE_1"
        )
      ),
    ...Array<null>(persQty)
      .fill(null)
      .map(() =>
        makeSyntheticOption(
          persChild,
          fallbackLabels.personal,
          "PERSONAL_REFERENCE_1"
        )
      ),
  ];
}

export function ReferenceQuantitySelects(props: ReferenceQuantitySelectsProps) {
  const {
    selectFieldDescription,
    selectFieldEndpoint,
    onUpdateSelection,
    isDisabled,
    checkArguments,
    checkChildren,
    customerGroup,
  } = props;
  const { t } = useTranslation();
  const { setCheckValidationError } = useOrderContext();

  const { data: templateData } = useClientPortalAPI(
    selectFieldEndpoint ? (selectFieldEndpoint as GetPaths) : null,
    { customer_group_id: customerGroup }
  );

  const templateOptions = useMemo<ReferenceTemplateOption[]>(
    () => (templateData as ReferenceTemplateOption[] | undefined) ?? [],
    [templateData]
  );

  const incomingTemplateId =
    typeof checkArguments?.template_id === "string"
      ? checkArguments.template_id
      : null;

  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(
    incomingTemplateId
  );

  useEffect(() => {
    setSelectedTemplateId(incomingTemplateId);
  }, [incomingTemplateId]);

  useEffect(() => {
    if (selectedTemplateId || templateOptions.length === 0) {
      return;
    }
    const defaultTemplate =
      templateOptions.find((opt) => opt.is_default) ?? templateOptions[0];
    if (defaultTemplate) {
      setSelectedTemplateId(defaultTemplate.value);
    }
  }, [selectedTemplateId, templateOptions]);

  const selectedTemplate = useMemo(
    () => templateOptions.find((opt) => opt.value === selectedTemplateId),
    [templateOptions, selectedTemplateId]
  );

  const validationKey = "REFERENCE_1_template";

  const onUpdateSelectionRef = useRef(onUpdateSelection);
  onUpdateSelectionRef.current = onUpdateSelection;
  const checkChildrenRef = useRef(checkChildren);
  checkChildrenRef.current = checkChildren;

  // Fallback labels for synthetic options when checkChildren lacks a localized `name`.
  // Held in a ref so the emit callback stays stable and does not re-fire on every render.
  const fallbackLabelsRef = useRef({
    professional: t("Professional Reference"),
    personal: t("Personal Reference"),
  });
  fallbackLabelsRef.current = {
    professional: t("Professional Reference"),
    personal: t("Personal Reference"),
  };

  const emitUpdate = useCallback(
    (templateId: string | null, profQty: number, persQty: number) => {
      const args: Record<string, unknown> = {};
      if (templateId) {
        args.template_id = templateId;
      }

      const syntheticOptions = buildSyntheticOptions(
        checkChildrenRef.current,
        profQty,
        persQty,
        fallbackLabelsRef.current
      );

      onUpdateSelectionRef.current(args, syntheticOptions);
    },
    []
  );

  useEffect(() => {
    // Skip emission until templates have loaded and a template is selected.
    // Emitting with 0/0 on mount briefly clears the parent's pricing before the API responds.
    if (!selectedTemplate) {
      return;
    }
    const profQty = selectedTemplate.professional_reference_quantity ?? 0;
    const persQty = selectedTemplate.personal_reference_quantity ?? 0;
    emitUpdate(selectedTemplateId, profQty, persQty);
  }, [emitUpdate, selectedTemplateId, selectedTemplate]);

  const hasError = !selectedTemplateId;

  useEffect(() => {
    setCheckValidationError(validationKey, hasError);

    return () => {
      setCheckValidationError(validationKey, false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasError]);

  const templateSelectItems = useMemo(
    () => templateOptions.map((opt) => ({ id: opt.value, label: opt.label })),
    [templateOptions]
  );

  const handleTemplateChange = useCallback((value: string | null) => {
    setSelectedTemplateId(value || null);
  }, []);

  const templateLabel =
    selectFieldDescription?.[0]?.[0] ?? t("Choose a template");

  return (
    <div className={styles.container}>
      <div className={styles.selectWrapper}>
        <Select
          items={templateSelectItems}
          label={templateLabel}
          value={selectedTemplateId}
          onChange={(value) => handleTemplateChange(value as string | null)}
          isDisabled={isDisabled || templateOptions.length === 0}
          testID="reference-template"
          info={
            <SelectFieldDescription
              selectFieldDescription={selectFieldDescription}
            />
          }
        >
          {(item) => (
            <SelectItem
              id={item.id}
              key={item.id}
              textValue={item.label}
              data-testid={`reference-template-option-${item.id}`}
            >
              {item.label}
            </SelectItem>
          )}
        </Select>
      </div>
      {hasError && (
        <p className={styles.errorMessage} role="alert">
          {t("Select a template.")}
        </p>
      )}
    </div>
  );
}

const styles = {
  container: css({
    padding: "var(--space-2) var(--space-3) var(--space-3)",
    margin: "var(--space-0) var(--space-05)",
    gap: "var(--space-2)",
    backgroundColor: "var(--background-accent-neutral-weakest)",
    display: "flex",
    flexDirection: "column",
  }),
  selectWrapper: css({
    display: "flex",
    flexDirection: "column",
    gap: "var(--space-1)",
  }),
  errorMessage: css({
    color: "var(--text-critical)",
    fontSize: "var(--font-size-xs)",
    margin: 0,
  }),
};
